import { parseScheduleJson } from '../components/business/WorkScheduleEditor'
import type { WeekSchedule } from '../components/business/WorkScheduleEditor'

export type { DaySchedule, WeekSchedule } from '../components/business/WorkScheduleEditor'
export { parseScheduleJson }

/** Check if the current date/time falls within a work schedule. Handles overnight shifts. */
export function isCurrentlyAtWork(scheduleJson?: string | null): boolean {
  if (!scheduleJson) return false
  try {
    const schedule = parseScheduleJson(scheduleJson)
    const now = new Date()
    const dayName = now.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase()
    const dayEntry = schedule[dayName]
    if (!dayEntry) return false

    const [startH, startM] = dayEntry.start.split(':').map(Number)
    const [endH, endM] = dayEntry.end.split(':').map(Number)
    const currentMinutes = now.getHours() * 60 + now.getMinutes()
    const startMinutes = startH * 60 + startM
    const endMinutes = endH * 60 + endM

    if (endMinutes <= startMinutes) {
      return currentMinutes >= startMinutes || currentMinutes <= endMinutes
    }
    return currentMinutes >= startMinutes && currentMinutes <= endMinutes
  } catch {
    return false
  }
}

/** Get a label for where the contact likely is right now. */
export function getCurrentLocationLabel(
  workScheduleJson?: string | null
): 'work' | 'home' | 'unknown' {
  if (!workScheduleJson) return 'unknown'
  return isCurrentlyAtWork(workScheduleJson) ? 'work' : 'home'
}

/** Format schedule as a compact multi-line summary. */
export function formatScheduleSummary(scheduleJson?: string | null): string {
  if (!scheduleJson) return 'No schedule set'
  try {
    const schedule = parseScheduleJson(scheduleJson)
    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
    const shortNames: Record<string, string> = {
      monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu',
      friday: 'Fri', saturday: 'Sat', sunday: 'Sun',
    }
    const lines = days
      .filter(d => schedule[d] != null)
      .map(d => `${shortNames[d]}: ${schedule[d]!.start}\u2013${schedule[d]!.end}`)
    return lines.length > 0 ? lines.join('\n') : 'No schedule set'
  } catch {
    return 'Invalid schedule'
  }
}

/** Get a compact inline schedule string (e.g., "Mon–Fri 9:00–17:00"). */
export function formatScheduleInline(schedule: WeekSchedule): string {
  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
  const shortNames: Record<string, string> = {
    monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu',
    friday: 'Fri', saturday: 'Sat', sunday: 'Sun',
  }
  const activeDays = days.filter(d => schedule[d] != null)
  if (activeDays.length === 0) return 'No schedule'

  // Check if all active days have the same hours
  const firstEntry = schedule[activeDays[0]]!
  const allSame = activeDays.every(d => {
    const entry = schedule[d]!
    return entry.start === firstEntry.start && entry.end === firstEntry.end
  })

  if (allSame && activeDays.length > 1) {
    const first = shortNames[activeDays[0]]
    const last = shortNames[activeDays[activeDays.length - 1]]
    // Check if consecutive
    const firstIdx = days.indexOf(activeDays[0])
    const lastIdx = days.indexOf(activeDays[activeDays.length - 1])
    if (lastIdx - firstIdx === activeDays.length - 1) {
      return `${first}\u2013${last} ${firstEntry.start}\u2013${firstEntry.end}`
    }
  }

  return activeDays.map(d => `${shortNames[d]} ${schedule[d]!.start}\u2013${schedule[d]!.end}`).join(', ')
}
